import axios from "axios";
import {
  applyEncryptionHeaders,
  decryptFromEnvelopeJson,
  encryptToEnvelopeJson,
  encryptTokenToBase64Envelope,
  isApiCryptoEnabled,
  looksLikeEnvelope,
  isTimeInfoRequest,
  isLargeImageBodyRequest,
} from "utils/apiCrypto";

if (isApiCryptoEnabled()) {
  axios.interceptors.request.use(
    async (config) => {
      config.headers = config.headers || {};
      if (isTimeInfoRequest(config) || isLargeImageBodyRequest(config)) {
        return config;
      }
      try {
        const method = String(config.method || "get").toUpperCase();
        const hasJsonBodyMethod =
          method === "POST" || method === "PUT" || method === "PATCH";
        const isFormData =
          typeof FormData !== "undefined" && config.data instanceof FormData;

        if (hasJsonBodyMethod && config.data != null && !isFormData) {
          const plain =
            typeof config.data === "string"
              ? config.data
              : JSON.stringify(config.data);
          config.data = JSON.parse(await encryptToEnvelopeJson(plain));
          applyEncryptionHeaders(config.headers, {
            encryptedBody: true,
            encryptedResponse: true,
          });
        } else {
          applyEncryptionHeaders(config.headers, { encryptedResponse: true });
        }

        const authHeader = String(config.headers.Authorization || "");
        if (authHeader.startsWith("Bearer ")) {
          const token = authHeader.slice(7).trim();
          if (token && !token.startsWith("eyJ")) {
            applyEncryptionHeaders(config.headers, { encryptedAuth: true });
          } else if (token) {
            const encToken = await encryptTokenToBase64Envelope(token);
            config.headers.Authorization = `Bearer ${encToken}`;
            applyEncryptionHeaders(config.headers, { encryptedAuth: true });
          }
        }
      } catch {
        // Keep existing behavior working if crypto env/key is misconfigured.
        // Request continues as plaintext JSON without crypto headers.
      }

      return config;
    },
    (err) => Promise.reject(err)
  );

  axios.interceptors.response.use(
    async (response) => {
      if (isTimeInfoRequest(response?.config) && !looksLikeEnvelope(response?.data)) {
        return response;
      }
      if (looksLikeEnvelope(response?.data)) {
        const plain = await decryptFromEnvelopeJson(response.data);
        response.data = JSON.parse(plain);
      }
      return response;
    },
    async (error) => {
      const res = error.response;
      if (looksLikeEnvelope(res?.data)) {
        try {
          const plain = await decryptFromEnvelopeJson(res.data);
          res.data = JSON.parse(plain);
        } catch {
          /* leave raw for error logging */
        }
      }
      return Promise.reject(error);
    }
  );
}
